import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const SPEED_MAP: Record<string, number> = {
  slow: 60,
  medium: 35,
  fast: 18,
};

const LogoMarquee: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const direction = c.direction || 'left';
  const speed = SPEED_MAP[c.speed || 'medium'] || 35;

  const logos = (c.logos || [
    { name: 'Acme Corp', image_url: '' },
    { name: 'GlobalTech', image_url: '' },
    { name: 'FinanceHub', image_url: '' },
    { name: 'HealthPlus', image_url: '' },
    { name: 'EduSmart', image_url: '' },
    { name: 'RetailPro', image_url: '' },
  ]) as Array<{ name: string; image_url: string }>;

  // Duplicate for seamless loop
  const logoList = [...logos, ...logos, ...logos];

  const animName = `marquee-${direction}`;
  const keyframes = `
    @keyframes ${animName} {
      0% { transform: translateX(${direction === 'left' ? '0' : '-33.333%'}); }
      100% { transform: translateX(${direction === 'left' ? '-33.333%' : '0'}); }
    }
  `;

  return (
    <section className="sb-render sb-render-logo-marquee" style={{ padding: '48px 0', background: 'var(--brand-bg-alt, #f8fafc)', borderTop: '1px solid rgba(0,0,0,0.06)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <style>{keyframes}</style>

      {sfHeadline(c) && (
        <div style={{ textAlign: 'center', marginBottom: 32, paddingLeft: 24, paddingRight: 24 }}>
          <InlineEditText
            tag="p"
            controlKey="headline"
            style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--brand-text-light, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.08em' }}
          >
            {sfHeadline(c, 'Trusted by leading companies')}
          </InlineEditText>
        </div>
      )}

      <div style={{ overflow: 'hidden', width: '100%', position: 'relative' }}>
        {/* Fade edges */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 80, zIndex: 2,
          background: 'linear-gradient(to right, var(--brand-bg-alt, #f8fafc), transparent)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, zIndex: 2,
          background: 'linear-gradient(to left, var(--brand-bg-alt, #f8fafc), transparent)',
          pointerEvents: 'none',
        }} />

        <div style={{
          display: 'flex',
          gap: 48,
          width: 'max-content',
          animation: `${animName} ${speed}s linear infinite`,
        }}>
          {logoList.map((logo, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 140,
                height: 64,
                padding: '8px 16px',
                filter: 'grayscale(100%)',
                opacity: 0.55,
                transition: 'opacity 0.2s, filter 0.2s',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.opacity = '1';
                (e.currentTarget as HTMLDivElement).style.filter = 'grayscale(0%)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.opacity = '0.55';
                (e.currentTarget as HTMLDivElement).style.filter = 'grayscale(100%)';
              }}
            >
              {logo.image_url ? (
                <img
                  src={logo.image_url}
                  alt={logo.name}
                  style={{ maxHeight: 48, maxWidth: 120, objectFit: 'contain' }}
                />
              ) : (
                <span style={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: 'var(--brand-text, #1e293b)',
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                }}>
                  {logo.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LogoMarquee;
